import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Clock } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getInsights, getOverview, lastNDaysRange, qk } from "../api.js";
import {
  comparePeriods,
  deltaLabel,
  METRIC_POLARITY,
  type CompareMetric,
  type PeriodDelta,
} from "@dxp/core";
import BilgiDugmesi from "../components/BilgiDugmesi.js";
import Card from "../components/Card.js";
import EmptyState, {
  ErrorState,
  SDK_EMPTY_DESCRIPTION,
  SDK_EMPTY_TITLE,
} from "../components/EmptyState.js";
import Table from "../components/Table.js";
import { LoadingBlock } from "../components/Spinner.js";
import { useIngestStatus } from "../components/KurulumDurumu.js";
import { useActivePid } from "../components/Layout.js";
import {
  formatDate,
  formatNumber,
  formatRatioPct,
  formatTL,
} from "../lib/format.js";

/**
 * §13.1 Genel Bakış — beyaz başlık şeridi + beyaz stat şeridi + 30 günlük eğilim
 * grafiği ve kanal/cihaz kırılımı; en üstte varsa `critical` insight bandı.
 *
 * Görsel dil referans DXP admin paneliyle birebirdir (bkz. Funnel sayfası):
 * #fafafa zemin, beyaz şeritler, rounded-2xl + #e5e7eb kartlar, marka turuncusu.
 */

/** Grafik serileri — oturum marka turuncusu, dönüşüm mavi (referans panel paleti). */
const COLOR_SESSIONS = "#f56600";
const COLOR_CONVERSIONS = "#3b82f6";

/** X ekseni etiketi: '2026-08-03' → '03.08' (yıl eksende yer kaplamasın). */
function dayTick(day: string): string {
  return formatDate(day).slice(0, 5);
}

export default function Overview() {
  const pid = useActivePid();
  const navigate = useNavigate();
  const { from, to } = lastNDaysRange(30);

  const overview = useQuery({
    queryKey: qk.overview(pid, from, to, true),
    queryFn: ({ signal }) => getOverview(pid, from, to, signal, true),
  });

  const insights = useQuery({
    queryKey: qk.insights(pid),
    queryFn: ({ signal }) => getInsights(pid, undefined, signal),
  });

  const data = overview.data;
  const critical = (insights.data?.items ?? []).filter(
    (i) =>
      i.severity === "critical" &&
      i.status !== "dismissed" &&
      i.status !== "resolved",
  );

  const sparkline = data?.sparkline ?? [];
  // §13 — boş durum mutlaka tasarlanmalı: hiç oturum yoksa SDK kurulum çağrısı.
  const isEmpty =
    !overview.isLoading && !overview.isError && (!data || data.sessions === 0);
  const hasData =
    !overview.isLoading &&
    !overview.isError &&
    data !== undefined &&
    data.sessions > 0;

  /*
   * §13 — boş durumun TEŞHİSİ. YALNIZ boş durumda sorulur (`enabled`): veri akan bir
   * projede kurulum sorusu yoktur ve 15 sn'lik yoklamayı boşuna kurmanın anlamı yok.
   * Hata/404 hâlinde `verdict.receiving=false` döner, yani eski davranışa (kurulum
   * çağrısı) güvenli şekilde düşülür.
   */
  const { verdict } = useIngestStatus(pid, { enabled: isEmpty });

  /** Stat şeridinin satır sözleşmesi — `metric` yalnız Δ rozeti olan satırlarda vardır. */
  interface StatSatiri {
    label: string;
    value: string;
    title: string;
    delta: PeriodDelta | null;
    metric?: CompareMetric;
    deltaTitle?: string;
    accent?: boolean;
  }

  const onceki = data?.previous ?? null;
  const rozet = (simdiki: number | undefined, gecmis: number | undefined) => {
    if (onceki === null || simdiki === undefined || gecmis === undefined)
      return null;
    return comparePeriods(simdiki, gecmis);
  };

  /**
   * §94.2 — rozet YÖNÜ metrikten bağımsız değildir: `METRIC_POLARITY` tablosu
   * "artışı iyi olmayan" bir metrik eklendiğinde rengin doğru dönmesini garanti eder.
   * Tablo `packages/core`da vardı ama HİÇBİR YERDEN okunmuyordu; renk sayfada elle
   * kodlanmıştı (denetim bulgusu B7) — yani tablonun koruduğu şey korunmuyordu.
   */
  const iyiMi = (metric: CompareMetric, direction: PeriodDelta["direction"]): boolean =>
    METRIC_POLARITY[metric] === "higherIsBetter" ? direction === "up" : direction === "down";

  /**
   * §94.2 — rozet ROTASI: "önceki dönem şu kadardı" cümlesi `title`'a girer.
   * Sabit "Önceki 30 güne göre değişim" metni, kullanıcıya yüzdeyi verip HAM sayıyı
   * saklıyordu; oysa %12'lik artışın 40'tan 45'e mi 40 binden 45 bine mi olduğu
   * kararı değiştirir (denetim bulgusu C2).
   */
  const oncekiIpucu = (bicimli: string | null): string =>
    bicimli === null
      ? "Önceki 30 güne göre değişim"
      : `Önceki 30 gün: ${bicimli}`;

  /** §13.1 — "üstte 5 stat": oturum, kullanıcı, CR, gelir, aktif insight sayısı. */
  const stats: StatSatiri[] = [
    {
      label: "Oturum",
      metric: "sessions",
      value: formatNumber(data?.sessions),
      title: "Son 30 gün toplam oturum",
      delta: rozet(data?.sessions, onceki?.sessions),
      deltaTitle: oncekiIpucu(onceki === null ? null : formatNumber(onceki.sessions)),
    },
    {
      label: "Kullanıcı",
      metric: "users",
      value: formatNumber(data?.users),
      title: "Tekil ziyaretçi",
      delta: rozet(data?.users, onceki?.users),
      deltaTitle: oncekiIpucu(onceki === null ? null : formatNumber(onceki.users)),
    },
    {
      label: "Dönüşüm Oranı",
      metric: "cr",
      value: formatRatioPct(data?.cr),
      title: `${formatNumber(data?.conversions)} dönüşüm`,
      delta: rozet(data?.cr, onceki?.cr),
      deltaTitle: oncekiIpucu(onceki === null ? null : formatRatioPct(onceki.cr)),
    },
    {
      label: "Gelir",
      metric: "revenue",
      value: formatTL(data?.revenue),
      title: "Son 30 gün toplam gelir",
      delta: rozet(data?.revenue, onceki?.revenue),
      deltaTitle: oncekiIpucu(onceki === null ? null : formatTL(onceki.revenue)),
    },
    {
      label: "Aktif bulgu",
      value: formatNumber(data?.activeInsights),
      title: "Yeni + görüldü durumundaki bulgular",
      accent: true,
      delta: null,
    },
  ];

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="genel-bakis-sayfasi">
      {/* başlık şeridi */}
      <div className="flex items-center justify-between border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">
            Genel Bakış
          </h1>
          <BilgiDugmesi testId="genel-bakis-bilgi">
            Oturum, kullanıcı, dönüşüm ve gelir son 30 günün toplamıdır (§13.1).
            Aktif bulgu sayısı Bulgular sayfasıyla aynı kaynaktan gelir.
          </BilgiDugmesi>
        </div>
        <span className="text-[12px] text-[#9ca3af]">
          {formatDate(from)} – {formatDate(to)} · son 30 gün
        </span>
      </div>

      {/* stat şeridi */}
      <div className="flex flex-wrap items-center gap-8 border-b border-[#e5e7eb] bg-white px-6 py-2.5">
        {overview.isLoading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">
            Veri yükleniyor…
          </span>
        ) : overview.isError ? (
          <span className="flex items-center gap-1.5 text-[12px] text-red-700">
            <AlertTriangle className="h-3.5 w-3.5" /> Veri alınamadı
          </span>
        ) : (
          <>
            {stats.map((s) => (
              <div
                key={s.label}
                className="flex items-center gap-2"
                title={s.title}
              >
                <span className="text-[12px] text-[#9ca3af]">{s.label}</span>
                <span
                  className={`num text-[13px] font-semibold ${
                    s.accent === true ? "text-brand" : "text-[#111827]"
                  }`}
                >
                  {s.value}
                </span>
                {s.delta !== null && s.delta !== undefined ? (
                  <span
                    data-testid={`genel-bakis-delta-${s.label}`}
                    className={`num rounded-full px-1.5 py-0.5 text-[11px] font-medium ${
                      s.delta.direction === "flat"
                        ? "bg-[#f3f4f6] text-[#6b7280]"
                        : s.metric !== undefined && iyiMi(s.metric, s.delta.direction)
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-red-50 text-red-700"
                    }`}
                    title={s.deltaTitle ?? "Önceki 30 güne göre değişim"}
                  >
                    {/*
                      §94.1 biçimi "▲ %12,4"tür. `deltaLabel` işareti de basar
                      ("+%12,4") çünkü OKSUZ kullanıldığı bir yer daha var (haftalık
                      e-posta, §26). Ok varken işaret İKİ KEZ söylenmiş olur —
                      burada yalnız büyüklük yazılır (denetim bulgusu C1).
                    */}
                    {s.delta.direction === "flat"
                      ? "≈"
                      : `${s.delta.direction === "up" ? "▲" : "▼"} ${deltaLabel(s.delta).replace(/^[+-]/, "")}`}
                  </span>
                ) : null}
              </div>
            ))}
            {hasData && onceki === null ? (
              <span
                data-testid="genel-bakis-karsilastirma-yok"
                className="text-[11px] text-[#9ca3af]"
              >
                karşılaştırılacak önceki dönem yok
              </span>
            ) : null}
          </>
        )}
      </div>

      {/* içerik */}
      <div className="px-6 py-8">
        {overview.isLoading ? (
          <LoadingBlock label="Genel bakış yükleniyor…" />
        ) : null}

        {overview.isError ? (
          <ErrorState
            description={(overview.error as Error).message}
            onRetry={() => void overview.refetch()}
          />
        ) : null}

        {/*
          §13 boş durum — AMA TEŞHİSLE BİRLİKTE.

          "SDK'yı ekleyin" metni tek başına YANILTICIYDI: raporlar `daily_agg`'den
          okunur ve oraya satır ancak oturum KAPANINCA girer (§11.5 — 30 dk
          hareketsizlik). Etiketi doğru kuran kullanıcı da ilk yarım saat bu metni
          görüyor, kurulumunu bozuk sanıp söküyordu. Olay ALINIYORSA artık farklı
          konuşulur: yapılacak iş kurulum değil, BEKLEMEKTİR.
        */}
        {isEmpty ? (
          verdict.receiving ? (
            <EmptyState
              icon={<Clock className="h-6 w-6 text-[#9ca3af]" />}
              title="Kurulum çalışıyor — ilk oturum bekleniyor"
              description={verdict.description}
              actionLabel="Kurulum durumunu aç"
              onAction={() => navigate("/ayarlar")}
            />
          ) : (
            <EmptyState
              title={SDK_EMPTY_TITLE}
              description={SDK_EMPTY_DESCRIPTION}
            />
          )
        ) : null}

        {hasData ? (
          <div className="space-y-6">
            {/*
              §91.5/3 — bulgular OKUNAMADIYSA sessiz kalınmaz. `insights` sorgusunun
              hatası hiç ele alınmıyordu: uç düştüğünde bant çizilmiyor ve kullanıcı
              "kritik bulgu yok" sanıyordu (denetim bulgusu C3). Yokluk ile
              bilinmezlik aynı ekran değildir.
            */}
            {insights.isError ? (
              <div
                data-testid="kritik-bant-okunamadi"
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#fde68a] bg-[#fffbeb] p-4"
              >
                <p className="text-[13px] text-[#92400e]">
                  Bulgular okunamadı — bu ekranda kritik bulgu olup olmadığı ŞU AN bilinmiyor.
                </p>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => void insights.refetch()}
                >
                  Yeniden dene
                </button>
              </div>
            ) : null}

            {/* §13.1 — en üstte varsa critical insight bandı */}
            {critical.length > 0 ? (
              <div
                role="alert"
                data-testid="kritik-bant"
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-l-4 border-red-500 bg-[#fef2f2] p-4"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <AlertTriangle
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0 text-red-500"
                  />
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold text-red-800">
                      {critical.length} kritik bulgu bekliyor
                    </p>
                    <p className="truncate text-[13px] text-red-700">
                      {critical[0]?.title}
                    </p>
                  </div>
                </div>
                <Link to="/bulgular" className="btn-primary shrink-0">
                  Bulgulara git
                </Link>
              </div>
            ) : null}

            {/* 30 günlük eğilim */}
            <Card
              title="Son 30 gün"
              subtitle="Oturum ve dönüşüm eğilimi"
              actions={
                <div className="flex items-center gap-4">
                  {[
                    { color: COLOR_SESSIONS, label: "Oturum" },
                    { color: COLOR_CONVERSIONS, label: "Dönüşüm" },
                  ].map((l) => (
                    <div key={l.label} className="flex items-center gap-1.5">
                      <span
                        aria-hidden="true"
                        className="h-2 w-2 rounded-sm"
                        style={{ background: l.color }}
                      />
                      <span className="text-[12px] text-[#6b7280]">
                        {l.label}
                      </span>
                    </div>
                  ))}
                </div>
              }
            >
              {sparkline.length === 0 ? (
                <EmptyState
                  compact
                  title="Grafik için yeterli gün yok"
                  description="En az iki günlük veri biriktiğinde eğilim burada çizilir."
                />
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={sparkline}
                      margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="gOturum"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor={COLOR_SESSIONS}
                            stopOpacity={0.28}
                          />
                          <stop
                            offset="100%"
                            stopColor={COLOR_SESSIONS}
                            stopOpacity={0.02}
                          />
                        </linearGradient>
                        <linearGradient
                          id="gDonusum"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor={COLOR_CONVERSIONS}
                            stopOpacity={0.24}
                          />
                          <stop
                            offset="100%"
                            stopColor={COLOR_CONVERSIONS}
                            stopOpacity={0.02}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="day"
                        tickFormatter={dayTick}
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        tickLine={false}
                        axisLine={{ stroke: "#e5e7eb" }}
                        minTickGap={24}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                        width={44}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          formatNumber(value),
                          name,
                        ]}
                        labelFormatter={(label: string) => formatDate(label)}
                        contentStyle={{
                          fontSize: 12,
                          borderRadius: 12,
                          border: "1px solid #e5e7eb",
                          boxShadow: "0 4px 16px rgba(17,24,39,0.08)",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="sessions"
                        name="Oturum"
                        stroke={COLOR_SESSIONS}
                        fill="url(#gOturum)"
                        strokeWidth={2}
                      />
                      <Area
                        type="monotone"
                        dataKey="conversions"
                        name="Dönüşüm"
                        stroke={COLOR_CONVERSIONS}
                        fill="url(#gDonusum)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>

            {/* kırılımlar */}
            <div className="grid gap-6 lg:grid-cols-2">
              <Card
                title="Kanal kırılımı"
                subtitle="Trafik kaynağına göre"
                flush
              >
                <Table
                  rows={data.byChannel ?? []}
                  rowKey={(r) => r.channel}
                  emptyTitle="Kanal verisi yok"
                  columns={[
                    {
                      key: "channel",
                      header: "Kanal",
                      render: (r) => (
                        <span className="font-medium text-[#111827]">
                          {r.channel}
                        </span>
                      ),
                    },
                    {
                      key: "sessions",
                      header: "Oturum",
                      align: "right",
                      render: (r) => formatNumber(r.sessions),
                    },
                    {
                      key: "cr",
                      header: "CR",
                      align: "right",
                      render: (r) =>
                        formatRatioPct(
                          r.sessions ? r.conversions / r.sessions : 0,
                        ),
                    },
                    {
                      key: "revenue",
                      header: "Gelir",
                      align: "right",
                      render: (r) => formatTL(r.revenue),
                    },
                  ]}
                />
              </Card>

              <Card
                title="Cihaz kırılımı"
                subtitle="Masaüstü / mobil / tablet"
                flush
              >
                <Table
                  rows={data.byDevice ?? []}
                  rowKey={(r) => r.device}
                  emptyTitle="Cihaz verisi yok"
                  columns={[
                    {
                      key: "device",
                      header: "Cihaz",
                      render: (r) => (
                        <span className="font-medium text-[#111827]">
                          {r.device}
                        </span>
                      ),
                    },
                    {
                      key: "sessions",
                      header: "Oturum",
                      align: "right",
                      render: (r) => formatNumber(r.sessions),
                    },
                    {
                      key: "cr",
                      header: "CR",
                      align: "right",
                      render: (r) =>
                        formatRatioPct(
                          r.sessions ? r.conversions / r.sessions : 0,
                        ),
                    },
                    {
                      key: "revenue",
                      header: "Gelir",
                      align: "right",
                      render: (r) => formatTL(r.revenue),
                    },
                  ]}
                />
              </Card>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
